import React, { useState, useEffect, useMemo } from 'react';
import { Button, Container, Grid, Snackbar, TextField, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Typography, Card, CardContent, Divider, List, ListItem, ListItemText, Box, Table, TableBody, TableContainer, TableHead, Paper, TableSortLabel } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { utils, writeFile } from 'xlsx';
import moment from "moment";
import { headingsTableGarantia } from '../../utils/default';
import MuiAlert from '@mui/material/Alert';
import { getPricesOnce } from '../../services/priceService';
import { getCuadrillasOnce } from '../../services/cuadrillasService';
import { collection, query as queryFirestore, where, orderBy, getDocs, getFirestore, doc, deleteDoc } from 'firebase/firestore';
import { StyledTableCell, StyledTableRow } from '../../utils/styled';

const firestore = getFirestore();

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const currentUser = JSON.parse(localStorage.getItem("currentUser"));

const pricesById = (prices) => {
    if (!prices) return {};
    return Array.isArray(prices) ? prices.reduce((acc, p) => { acc[p.id] = p; return acc; }, {}) : prices;
};

const priceValueServices = (services, tipoDia, pricesMap) => {
    if (!services || !Array.isArray(services)) return 0;
    return services.reduce((total, service) => {
        if (!pricesMap || !pricesMap[service.id]) return total;
        const typeValue = typeof service.value === 'boolean';
        const priceHogar = tipoDia === 'Semana'
            ? parseInt(pricesMap[service.id]?.hogar, 10) || 0
            : parseInt(pricesMap[service.id]?.feriado_domingo_hogar, 10) || 0;
        return total + (typeValue ? priceHogar : priceHogar * (parseInt(service.value, 10) || 0));
    }, 0);
};

const formatCurrency = (amount) => {
    if (!amount) return '₲ 0';
    const n = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(n)) return '₲ 0';
    return new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
};

const getCuadrillaPercentage = (cuadrillaName, cuadrillasList) => {
    if (!cuadrillaName || !cuadrillasList) return 0;
    const match = cuadrillasList.find(c => {
        const cNum = c.numero || '';
        return cNum.split(' ')[0] === cuadrillaName.split(' ')[0];
    });
    return match ? (parseFloat(match.porcentajeParticipacion) || 0) : 0;
};

function GarantiasExcel() {
    const [state, setState] = useState({
        dateTo: moment(),
        dateFrom: moment().startOf('month'),
    });
    const [alertValidationDate, setAlertValidationDate] = useState('');
    const [snackbarInfo, setSnackbarInfo] = useState({ open: false, text: '', type: 'neutral' });
    const [prices, setPrices] = useState([]);
    const [cuadrillas, setCuadrillas] = useState([]);
    const [garantias, setGarantias] = useState([]);
    const [sortBy, setSortBy] = useState(null);
    const [sortOrder, setSortOrder] = useState('desc');

    // Modals
    const [selectedGarantia, setSelectedGarantia] = useState(null);
    const [openDetails, setOpenDetails] = useState(false);
    const [garantiaToDelete, setGarantiaToDelete] = useState(null);
    const [openDelete, setOpenDelete] = useState(false);

    useEffect(() => {
        getPricesOnce(setPrices);
        getCuadrillasOnce(setCuadrillas, { soloActivas: false });
    }, []);

    const pricesMap = useMemo(() => pricesById(prices), [prices]);

    const onChangeDateTo = (value) => {
        const d = new Date(value);
        const isBeforeDate = moment(d).isBefore(moment(new Date()).add(1, 'days'));
        if (isBeforeDate) {
            setState(prevState => ({ ...prevState, dateTo: value }));
            setAlertValidationDate('');
        } else {
            setAlertValidationDate('La fecha debe ser menor o igual a la actual');
        }
    };

    const onChangeDateFrom = (value) => {
        const d = new Date(value);
        const isBeforeDate = moment(d).isBefore(moment(new Date()).add(1, 'days'));
        if (isBeforeDate) {
            setState(prevState => ({ ...prevState, dateFrom: value }));
            setAlertValidationDate('');
        } else {
            setAlertValidationDate('La fecha debe ser menor o igual a la actual');
        }
    };

    const fetchGarantias = async () => {
        if (!state.dateFrom || !state.dateTo) {
            setSnackbarInfo({ open: true, text: 'Debe seleccionar un rango de fechas', type: 'error' });
            return;
        }
        try {
            const dateFrom = moment(state.dateFrom).format("YYYYMMDD");
            const dateTo = moment(state.dateTo).format("YYYYMMDD");

            const garantiasRef = collection(firestore, 'garantias');
            const q = queryFirestore(
                garantiasRef,
                where('date', '>=', dateFrom),
                where('date', '<=', dateTo),
                orderBy('date')
            );

            const querySnapshot = await getDocs(q);
            const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setGarantias(data);
        } catch (error) {
            console.error('Error al obtener garantías:', error);
            setSnackbarInfo({ open: true, text: 'Error al cargar garantías', type: 'error' });
        }
    };

    useEffect(() => {
        fetchGarantias();
    }, []);

    const processedGarantias = useMemo(() => {
        return garantias.map(item => {
            const data = item[0] || item["0"] || item;

            const fecha1 = moment(data?.otRelacionada?.date);
            const fecha2 = moment(data?.date);

            const diasDiferencia = fecha2.isValid() && fecha1.isValid()
                ? fecha2.diff(fecha1, 'days')
                : 'N/A';

            const originalTotal = priceValueServices(data?.services, data?.tipoDia, pricesMap);
            const targetCuadrilla = data?.otRelacionada?.cuadrilla || data?.cuadrilla;
            const pct = getCuadrillaPercentage(targetCuadrilla, cuadrillas);
            const totalWithPct = originalTotal * (pct / 100);

            return {
                id: item.id,
                fecha1: fecha1.isValid() ? fecha1.format("DD/MM/YYYY") : 'N/A',
                contrato1: data?.otRelacionada?.contrato || 'N/A',
                nroOt1: data?.otRelacionada?.nroOt || 'N/A',
                cliente1: data?.otRelacionada?.cliente || 'N/A',
                cuadrilla1: data?.otRelacionada?.cuadrilla || 'N/A',
                comments1: data?.otRelacionada?.comments || '',
                fecha2: fecha2.isValid() ? fecha2.format("DD/MM/YYYY") : 'N/A',
                contrato2: data?.contrato || 'N/A',
                nroOt2: data?.nroOt || 'N/A',
                cliente2: data?.cliente || 'N/A',
                cuadrilla2: data?.cuadrilla || 'N/A',
                comments2: data?.comments || '',
                diasDiferencia,
                montoOriginal: originalTotal,
                porcentajeParticipacion: pct,
                montoFinal: totalWithPct,
                rawServices: data?.services || [],
                tipoDia: data?.tipoDia || 'Semana',
                tipoCliente: data?.tipoCliente || 'HOME'
            };
        });
    }, [garantias, pricesMap, cuadrillas]);

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('desc');
        }
    };

    const sortedGarantias = useMemo(() => {
        if (!sortBy) return processedGarantias;
        return [...processedGarantias].sort((a, b) => {
            let valA = a[sortBy];
            let valB = b[sortBy];
            if (typeof valA === 'string') {
                return sortOrder === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
            }
            return sortOrder === 'asc' ? valA - valB : valB - valA;
        });
    }, [processedGarantias, sortBy, sortOrder]);

    const handleDownloadExcel = () => {
        if (processedGarantias.length === 0) {
            setSnackbarInfo({ open: true, text: 'No hay datos para descargar', type: 'error' });
            return;
        }

        const wb = utils.book_new();
        const ws = utils.json_to_sheet([]);

        const dataToExport = processedGarantias.map(g => ({
            fecha1: g.fecha1,
            contrato1: g.contrato1,
            nroOt1: g.nroOt1,
            cliente1: g.cliente1,
            cuadrilla1: g.cuadrilla1,
            comments1: g.comments1,
            fecha2: g.fecha2,
            contrato2: g.contrato2,
            nroOt2: g.nroOt2,
            cliente2: g.cliente2,
            cuadrilla2: g.cuadrilla2,
            comments2: g.comments2,
            diasDiferencia: g.diasDiferencia,
            total: g.montoFinal
        }));

        utils.sheet_add_aoa(ws, headingsTableGarantia);
        utils.sheet_add_json(ws, dataToExport, { origin: 'A2', skipHeader: true });
        utils.book_append_sheet(wb, ws, 'Garantias');

        const dateNow = moment().format("YYYYMMDD");
        writeFile(wb, `garantias-${dateNow}.xlsx`);
    };

    const handleDeleteClick = (garantia) => {
        setGarantiaToDelete(garantia);
        setOpenDelete(true);
    };

    const confirmDelete = async () => {
        if (!garantiaToDelete) return;
        try {
            await deleteDoc(doc(firestore, "garantias", String(garantiaToDelete.id)));
            setSnackbarInfo({ open: true, text: 'Garantía eliminada correctamente', type: 'success' });
            setGarantias(prev => prev.filter(g => g.id !== garantiaToDelete.id));
        } catch (error) {
            console.error("Error al eliminar la garantía:", error);
            setSnackbarInfo({ open: true, text: 'Error al eliminar la garantía', type: 'error' });
        } finally {
            setOpenDelete(false);
            setGarantiaToDelete(null);
        }
    };

    const handleViewDetails = (garantia) => {
        setSelectedGarantia(garantia);
        setOpenDetails(true);
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbarInfo(prev => ({ ...prev, open: false }));
    };

    const sortLabelSx = {
        color: 'white !important',
        '& .MuiTableSortLabel-icon': { color: 'white !important', opacity: 1 },
        '&:hover': { color: 'rgba(255,255,255,0.85) !important' },
        '&:hover .MuiTableSortLabel-icon': { color: 'rgba(255,255,255,0.85) !important', opacity: 1 },
        '&.Mui-active': { color: 'white !important' },
        '&.Mui-active .MuiTableSortLabel-icon': { color: 'white !important', opacity: 1 },
    };

    const isAdmin = currentUser?.rol === 'admin';

    return (
        <Container component="main" maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                Garantías
            </Typography>

            <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Grid item xs={12} sm={3}>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker
                            className="date-selector"
                            label="Fecha Desde"
                            inputFormat="DD/MM/YYYY"
                            value={state.dateFrom}
                            onChange={onChangeDateFrom}
                            renderInput={(params) => <TextField {...params} fullWidth />}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker
                            className="date-selector-to"
                            label="Fecha Hasta"
                            inputFormat="DD/MM/YYYY"
                            value={state.dateTo}
                            onChange={onChangeDateTo}
                            renderInput={(params) => <TextField {...params} fullWidth />}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6} sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        onClick={fetchGarantias}
                        variant="contained"
                        color="primary"
                        startIcon={<SearchIcon />}
                        disabled={state.dateFrom === null || state.dateTo === null}
                    >
                        Buscar
                    </Button>
                    <Button
                        onClick={handleDownloadExcel}
                        variant="contained"
                        color="secondary"
                        startIcon={<FileDownloadIcon />}
                        disabled={state.dateFrom === null || state.dateTo === null}
                    >
                        Descargar planilla
                    </Button>
                </Grid>
            </Grid>

            {alertValidationDate !== '' && (
                <Box sx={{ mb: 2 }}>
                    <MuiAlert severity="error">{alertValidationDate}</MuiAlert>
                </Box>
            )}

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <StyledTableRow>
                            <StyledTableCell>Contrato</StyledTableCell>
                            <StyledTableCell>Cliente 2da OT</StyledTableCell>
                            <StyledTableCell align="left">
                                <TableSortLabel
                                    active={sortBy === 'cuadrilla1'}
                                    direction={sortBy === 'cuadrilla1' ? sortOrder : 'desc'}
                                    onClick={() => handleSort('cuadrilla1')}
                                    sx={sortLabelSx}
                                >
                                    Cuadrilla 1ra OT
                                </TableSortLabel>
                            </StyledTableCell>
                            <StyledTableCell>Cuadrilla 2da OT</StyledTableCell>
                            <StyledTableCell align="center">Fecha 1ra OT</StyledTableCell>
                            <StyledTableCell align="center">Fecha 2da OT</StyledTableCell>
                            <StyledTableCell align="center">
                                <TableSortLabel
                                    active={sortBy === 'diasDiferencia'}
                                    direction={sortBy === 'diasDiferencia' ? sortOrder : 'desc'}
                                    onClick={() => handleSort('diasDiferencia')}
                                    sx={sortLabelSx}
                                >
                                    Días Dif.
                                </TableSortLabel>
                            </StyledTableCell>
                            {isAdmin && <StyledTableCell align="right">Monto Original</StyledTableCell>}
                            {isAdmin && <StyledTableCell align="center">% Part.</StyledTableCell>}
                            <StyledTableCell align="right">
                                <TableSortLabel
                                    active={sortBy === 'montoFinal'}
                                    direction={sortBy === 'montoFinal' ? sortOrder : 'desc'}
                                    onClick={() => handleSort('montoFinal')}
                                    sx={sortLabelSx}
                                >
                                    Monto Final
                                </TableSortLabel>
                            </StyledTableCell>
                            <StyledTableCell align="center">Acciones</StyledTableCell>
                        </StyledTableRow>
                    </TableHead>
                    <TableBody>
                        {sortedGarantias.length === 0 ? (
                            <StyledTableRow>
                                <StyledTableCell colSpan={isAdmin ? 11 : 9} align="center">
                                    No se encontraron garantías en este rango de fechas.
                                </StyledTableCell>
                            </StyledTableRow>
                        ) : (
                            sortedGarantias.map((row) => (
                                <StyledTableRow key={row.id}>
                                    <StyledTableCell>{row.contrato2}</StyledTableCell>
                                    <StyledTableCell>{row.cliente2}</StyledTableCell>
                                    <StyledTableCell>{row.cuadrilla1}</StyledTableCell>
                                    <StyledTableCell>{row.cuadrilla2}</StyledTableCell>
                                    <StyledTableCell align="center">{row.fecha1}</StyledTableCell>
                                    <StyledTableCell align="center">{row.fecha2}</StyledTableCell>
                                    <StyledTableCell align="center">{row.diasDiferencia}</StyledTableCell>
                                    {isAdmin && <StyledTableCell align="right">{formatCurrency(row.montoOriginal)}</StyledTableCell>}
                                    {isAdmin && <StyledTableCell align="center">{row.porcentajeParticipacion}%</StyledTableCell>}
                                    <StyledTableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(row.montoFinal)}</StyledTableCell>
                                    <StyledTableCell align="center">
                                        <IconButton color="primary" onClick={() => handleViewDetails(row)}>
                                            <VisibilityIcon />
                                        </IconButton>
                                        {isAdmin && (
                                            <IconButton color="error" onClick={() => handleDeleteClick(row)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        )}
                                    </StyledTableCell>
                                </StyledTableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Details Modal */}
            <Dialog open={openDetails} onClose={() => setOpenDetails(false)} maxWidth="md" fullWidth>
                <DialogTitle>Detalle de Garantía (ID: {selectedGarantia?.id})</DialogTitle>
                <DialogContent dividers>
                    {selectedGarantia && (
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography color="textSecondary" gutterBottom variant="subtitle2">
                                            1RA OT (ORIGINAL)
                                        </Typography>
                                        <Divider sx={{ mb: 1.5 }} />
                                        <Typography variant="body2"><strong>Contrato:</strong> {selectedGarantia.contrato1}</Typography>
                                        <Typography variant="body2"><strong>Nro OT:</strong> {selectedGarantia.nroOt1}</Typography>
                                        <Typography variant="body2"><strong>Cliente:</strong> {selectedGarantia.cliente1}</Typography>
                                        <Typography variant="body2"><strong>Cuadrilla:</strong> {selectedGarantia.cuadrilla1}</Typography>
                                        <Typography variant="body2"><strong>Fecha:</strong> {selectedGarantia.fecha1}</Typography>
                                        <Typography variant="body2" sx={{ mt: 1 }}><strong>Comentarios:</strong> {selectedGarantia.comments1 || 'Sin comentarios'}</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography color="textSecondary" gutterBottom variant="subtitle2">
                                            2DA OT (GARANTÍA)
                                        </Typography>
                                        <Divider sx={{ mb: 1.5 }} />
                                        <Typography variant="body2"><strong>Contrato:</strong> {selectedGarantia.contrato2}</Typography>
                                        <Typography variant="body2"><strong>Nro OT:</strong> {selectedGarantia.nroOt2}</Typography>
                                        <Typography variant="body2"><strong>Cliente:</strong> {selectedGarantia.cliente2}</Typography>
                                        <Typography variant="body2"><strong>Cuadrilla:</strong> {selectedGarantia.cuadrilla2}</Typography>
                                        <Typography variant="body2"><strong>Fecha:</strong> {selectedGarantia.fecha2}</Typography>
                                        <Typography variant="body2" sx={{ mt: 1 }}><strong>Comentarios:</strong> {selectedGarantia.comments2 || 'Sin comentarios'}</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography color="textSecondary" gutterBottom variant="subtitle2">
                                            CÁLCULO Y LIQUIDACIÓN
                                        </Typography>
                                        <Divider sx={{ mb: 1.5 }} />
                                        <Grid container spacing={2}>
                                            <Grid item xs={4}>
                                                <Typography variant="body2"><strong>Días de Diferencia:</strong> {selectedGarantia.diasDiferencia}</Typography>
                                                <Typography variant="body2"><strong>Tipo Cliente:</strong> {selectedGarantia.tipoCliente}</Typography>
                                                <Typography variant="body2"><strong>Tipo Día:</strong> {selectedGarantia.tipoDia}</Typography>
                                            </Grid>
                                            <Grid item xs={8} sx={{ textAlign: 'right' }}>
                                                {isAdmin && (
                                                    <>
                                                        <Typography variant="body2"><strong>Monto Original:</strong> {formatCurrency(selectedGarantia.montoOriginal)}</Typography>
                                                        <Typography variant="body2"><strong>Participación Cuadrilla:</strong> {selectedGarantia.porcentajeParticipacion}%</Typography>
                                                    </>
                                                )}
                                                <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                                                    <strong>Monto Final:</strong> {formatCurrency(selectedGarantia.montoFinal)}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Servicios en 2da OT</Typography>
                                <Paper variant="outlined">
                                    <List dense>
                                        {selectedGarantia.rawServices.length === 0 ? (
                                            <ListItem><ListItemText primary="No hay servicios registrados" /></ListItem>
                                        ) : (
                                            selectedGarantia.rawServices.map((service, idx) => {
                                                const servicePrice = pricesMap[service.id];
                                                const priceHogar = selectedGarantia.tipoDia === 'Semana'
                                                    ? parseInt(servicePrice?.hogar, 10) || 0
                                                    : parseInt(servicePrice?.feriado_domingo_hogar, 10) || 0;
                                                const count = typeof service.value === 'boolean' ? 1 : parseInt(service.value, 10) || 0;
                                                const originalVal = priceHogar * count;
                                                const isFixed = servicePrice?.montoFijo === true || servicePrice?.montoFijo === "true";
                                                const isSemana = selectedGarantia.tipoDia === 'Semana';
                                                const customFixedSemana = parseInt(servicePrice?.montoFijoValor, 10);
                                                const customFixedFeriado = parseInt(servicePrice?.montoFijoFeriadoValor, 10);
                                                let fixedUnit = priceHogar;
                                                if (isFixed) {
                                                    if (isSemana) {
                                                        fixedUnit = !isNaN(customFixedSemana) ? customFixedSemana : priceHogar;
                                                    } else {
                                                        if (!isNaN(customFixedFeriado)) {
                                                            fixedUnit = customFixedFeriado;
                                                        } else if (!isNaN(customFixedSemana)) {
                                                            fixedUnit = customFixedSemana;
                                                        } else {
                                                            fixedUnit = priceHogar;
                                                        }
                                                    }
                                                }
                                                const finalVal = isFixed ? (fixedUnit * count) : originalVal * (selectedGarantia.porcentajeParticipacion / 100);

                                                return (
                                                    <ListItem key={idx} divider={idx < selectedGarantia.rawServices.length - 1}>
                                                        <ListItemText 
                                                            primary={`${service.id} (Cantidad: ${service.value})`} 
                                                            secondary={isAdmin ? `Precio unitario: ${formatCurrency(priceHogar)}` : null} 
                                                        />
                                                        <Box sx={{ textAlign: 'right' }}>
                                                            {isAdmin && <Typography variant="body2">Original: {formatCurrency(originalVal)}</Typography>}
                                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Liquidado: {formatCurrency(finalVal)}</Typography>
                                                        </Box>
                                                    </ListItem>
                                                );
                                            })
                                        )}
                                    </List>
                                </Paper>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDetails(false)} color="primary">Cerrar</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
                <DialogTitle>Confirmar Eliminación</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Está seguro que desea eliminar la garantía del contrato <strong>{garantiaToDelete?.contrato2}</strong>? Esta acción no se puede deshacer.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDelete(false)} color="inherit">Cancelar</Button>
                    <Button onClick={confirmDelete} color="error" variant="contained">Eliminar</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbarInfo.open} autoHideDuration={2000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={handleCloseSnackbar} severity={snackbarInfo.type} sx={{ width: '100%' }}>
                    {snackbarInfo.text}
                </Alert>
            </Snackbar>
        </Container>
    );
}

export default GarantiasExcel;
